import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createCampaign, updateCampaign } from '../../services/campaignService';
import { fetchTemplatesByGenre, incrementTemplateUseCount, templateToCampaignData } from '../../services/templateService';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import QuickActions from '../../components/ui/QuickActions';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import StepIndicator from './components/StepIndicator';
import CampaignBasics from './components/CampaignBasics';
import BudgetGoals from './components/BudgetGoals';
import ContentRequirements from './components/ContentRequirements';
import InfluencerSelection from './components/InfluencerSelection';
import TimelineLaunch from './components/TimelineLaunch';
import TemplateManager from './components/TemplateManager';

const CampaignCreationWizard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftCampaignId, setDraftCampaignId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [campaignData, setCampaignData] = useState({
    name: '',
    description: '',
    musicTrackUrl: '',
    genre: '',
    objectives: [],
    budget: '',
    costPerEngagement: '',
    paymentSchedule: 'milestone',
    contentTypes: [],
    creativeGuidelines: '',
    brandAssets: [],
    minFollowers: '',
    maxFollowers: '',
    minEngagement: '',
    contentSpecialization: [],
    selectedInfluencers: [],
    hashtags: [],
    startDate: '',
    endDate: '',
    milestones: [],
    proposalTemplate: '',
    brandGuidelines: '',
    hashtagRequirements: '',
    goals: ''
  });

  const steps = [
    { number: 1, title: 'Campaign Basics', icon: 'FileText' },
    { number: 2, title: 'Budget & Goals', icon: 'DollarSign' },
    { number: 3, title: 'Content Requirements', icon: 'Image' },
    { number: 4, title: 'Influencer Selection', icon: 'Users' },
    { number: 5, title: 'Timeline & Launch', icon: 'Calendar' }
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Load templates when genre changes
  useEffect(() => {
    if (campaignData?.genre) {
      loadTemplatesForGenre(campaignData?.genre);
    }
  }, [campaignData?.genre]);

  const loadTemplatesForGenre = async (genre) => {
    try {
      setLoadingTemplates(true);
      const templates = await fetchTemplatesByGenre(genre);
      setAvailableTemplates(templates);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleLoadTemplate = async (template) => {
    try {
      const templateData = templateToCampaignData(template);
      setCampaignData(prev => ({
        ...prev,
        ...templateData,
        name: prev?.name || '',
        musicTrackUrl: prev?.musicTrackUrl || ''
      }));
      
      await incrementTemplateUseCount(template?.id);
      setShowTemplateModal(false);
      alert(`Template "${template?.template_name}" loaded successfully!`);
    } catch (error) {
      console.error('Error loading template:', error);
      alert('Failed to load template. Please try again.');
    }
  };

  const updateCampaignData = (field, value) => {
    setCampaignData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!campaignData?.name?.trim()) newErrors.name = 'Campaign name is required';
        if (!campaignData?.description?.trim()) newErrors.description = 'Description is required';
        if (!campaignData?.genre) newErrors.genre = 'Genre is required';
        if (campaignData?.objectives?.length === 0) newErrors.objectives = 'Select at least one objective';
        break;
      case 2:
        if (!campaignData?.budget || parseFloat(campaignData?.budget) <= 0) {
          newErrors.budget = 'Budget must be greater than 0';
        }
        if (!campaignData?.goals?.trim()) newErrors.goals = 'Campaign goals are required';
        break;
      case 3:
        if (campaignData?.contentTypes?.length === 0) {
          newErrors.contentTypes = 'Select at least one content type';
        }
        break;
      case 4:
        if (campaignData?.selectedInfluencers?.length === 0) {
          newErrors.selectedInfluencers = 'Select at least one influencer';
        }
        break;
      case 5:
        if (!campaignData?.startDate) newErrors.startDate = 'Start date is required';
        if (!campaignData?.endDate) newErrors.endDate = 'End date is required';
        if (campaignData?.startDate && campaignData?.endDate) {
          if (new Date(campaignData.endDate) <= new Date(campaignData.startDate)) {
            newErrors.endDate = 'End date must be after start date';
          }
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 5) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSavingDraft(true);

      const draftData = {
        ...campaignData,
        status: 'draft',
        proposalTemplate: campaignData?.proposalTemplate || 'Default proposal template',
        brandGuidelines: campaignData?.creativeGuidelines || campaignData?.brandGuidelines || '',
        hashtagRequirements: campaignData?.hashtags?.join(', ') || '',
        goals: campaignData?.goals || campaignData?.objectives?.join(', ') || ''
      };

      if (draftCampaignId) {
        await updateCampaign(draftCampaignId, draftData);
      } else {
        const campaign = await createCampaign(draftData);
        setDraftCampaignId(campaign?.id);
      }

      alert('Draft saved successfully!');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) {
      return;
    }

    try {
      setLoading(true);

      const finalData = {
        ...campaignData,
        status: 'active',
        proposalTemplate: campaignData?.proposalTemplate || 'We would like to collaborate with you on our music campaign.',
        brandGuidelines: campaignData?.creativeGuidelines || campaignData?.brandGuidelines || 'Please follow our brand guidelines.',
        hashtagRequirements: campaignData?.hashtags?.join(', ') || '',
        goals: campaignData?.goals || campaignData?.objectives?.join(', ') || ''
      };

      if (draftCampaignId) {
        await updateCampaign(draftCampaignId, finalData);
      } else {
        await createCampaign(finalData);
      }

      alert('Campaign created successfully!');
      navigate('/campaign-management');
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert('Failed to create campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CampaignBasics
            data={campaignData}
            updateData={updateCampaignData}
            errors={errors}
          />
        );
      case 2:
        return (
          <BudgetGoals
            data={campaignData}
            updateData={updateCampaignData}
            errors={errors}
          />
        );
      case 3:
        return (
          <ContentRequirements
            data={campaignData}
            updateData={updateCampaignData}
            errors={errors}
          />
        );
      case 4:
        return (
          <InfluencerSelection
            data={campaignData}
            updateData={updateCampaignData}
            errors={errors}
          />
        );
      case 5:
        return (
          <TimelineLaunch
            data={campaignData}
            updateData={updateCampaignData}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <MobileNav />
      <QuickActions />

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/campaign-management')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Back to Campaigns</span>
          </button>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            <Icon name="Sparkles" size={20} />
            Create Campaign
          </h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowTemplateModal(true)}
              iconName="FileText"
              iconPosition="left"
              disabled={!campaignData?.genre}
            >
              Load Template
            </Button>
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={savingDraft}
              iconName="Save"
              iconPosition="left"
            >
              {savingDraft ? 'Saving...' : 'Save Draft'}
            </Button>
          </div>
          <p className="text-muted-foreground">Follow the steps to set up your music promotion campaign</p>
        </div>

        {/* Step Indicator */}
        <StepIndicator steps={steps} currentStep={currentStep} />

        {/* Step Content */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 md:p-8 mb-6">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          <div className="flex gap-3">
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={handlePrevious}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={savingDraft}
              iconName="Save"
              iconPosition="left"
            >
              {savingDraft ? 'Saving...' : 'Save Draft'}
            </Button>

            {currentStep < 5 ? (
              <Button
                onClick={handleNext}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next Step
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                loading={loading}
                iconName="Check"
                iconPosition="left"
                variant="success"
              >
                Launch Campaign
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Template Manager Modal */}
      {showTemplateModal && (
        <TemplateManager
          genre={campaignData?.genre}
          templates={availableTemplates}
          loading={loadingTemplates}
          onLoadTemplate={handleLoadTemplate}
          onClose={() => setShowTemplateModal(false)}
          currentCampaignData={campaignData}
        />
      )}
    </div>
  );
};

export default CampaignCreationWizard;
